// src/pages/DocumentManagementSystem/utils/dmsToPdf.js
//
// Phase 9 — DMS document PDFs are built HERE, in the browser, with jsPDF
// (the EPO way — see ../../ExpensePurchaseOrder/epoToPdf.js), then uploaded
// to POST /api/dms/{id}/pdf and stored. The server never renders them: the
// approval e-mail attaches the stored bytes and GET /api/dms/{id}/pdf serves
// them.
//
// Data: GET /api/dms/{id}/pdf-data (DmsPdfDataBuilder) — the same model the
// old server templates (Templates/pdf/EcnTemplate.html and
// StandardDocTemplate.html) were filled from. This file ports those two
// templates section by section, in the same order, with the same labels:
//   header band (logo + doc-type title + "No. {DocID}") → metadata grid →
//   [ECN: parts table → customer block] → content sections →
//   (new page) Associated Documents → Attachments → [ECN: ECN Tasks] →
//   Revision History → Approvals & Notifications.
// Per-page footer: "{DocID} Rev {Rev}" | status | "This copy printed on …
// Page X of Y"; diagonal "UNAPPROVED" watermark unless Status 1 / 2.
//
// Go-live parity (2026-09-24, audit DMS#7): rich text is laid out with its
// pictures, tables, headings, bold / italic / underline and lists by
// ./dmsRichTextPdf.js (jsPDF text + autotable grids + JPEG pictures — no
// doc.html() / html2canvas, so text stays selectable and the file small).
// The plain-text rendering below (htmlToPlainText) is the fallback when a
// section cannot be laid out that way.

import jsPDF from "jspdf";
import { formatRev } from "./dmsRev";
import autoTable from "jspdf-autotable";
import { renderRichHtml, createPictureCache } from "./dmsRichTextPdf";
import {
    EPO_COMPANY_CODES,
    getEpoCompanyBrand,
    loadBrandLogo,
} from "../../ExpensePurchaseOrder/epoCompanyBranding";

// ── Branding ─────────────────────────────────────────────────────────
// DMS stores sswhite / shukla / sss. Same mapping as the server's old
// DmsPdfService logo switch (Round DMS-UAT-9), onto the shared EPO brands.
export const resolveDmsBrand = (logoType) => {
    const lt = String(logoType || "").trim().toLowerCase();
    const code = lt === "shukla"
        ? EPO_COMPANY_CODES.SHUKLA_MEDICAL
        : (lt === "sss" || lt.includes("surgical"))
            ? EPO_COMPANY_CODES.SHUKLA_SURGICAL_SUPPORT
            : EPO_COMPANY_CODES.SSWHITE;
    return getEpoCompanyBrand(code);
};

// ── Text safety ──────────────────────────────────────────────────────
// jsPDF's built-in Helvetica is WinAnsi-encoded; characters outside it
// print as garbage. Map the common ones, replace anything else with "?".
const CHAR_MAP = {
    "\u2713": "(done)", "\u2714": "(done)", "\u2264": "<=", "\u2265": ">=",
    "\u2192": "->", "\u2190": "<-", "\u2248": "~", "\u2260": "!=",
    "\u2212": "-", "\u00A0": " ", "\u200B": "", "\uFEFF": "",
};
const WIN_ANSI_EXTRA = new Set("€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ".split(""));
export const pdfSafe = (value) => {
    if (value === null || value === undefined) return "";
    let out = "";
    for (const ch of String(value)) {
        if (CHAR_MAP[ch] !== undefined) { out += CHAR_MAP[ch]; continue; }
        const code = ch.codePointAt(0);
        // Control characters are dropped, as a browser does (legacy rows can
        // carry NUL-filled blobs); printable characters Helvetica cannot draw
        // become "?".
        if (code === 9 || code === 10 || code === 13) { out += ch; continue; }
        if (code < 32 || (code >= 127 && code <= 159)) continue;
        if ((code >= 32 && code <= 126) || (code >= 160 && code <= 255) || WIN_ANSI_EXTRA.has(ch)) out += ch;
        else out += "?";
    }
    return out;
};

// ── Rich text → plain text ───────────────────────────────────────────
// Keeps paragraphs, <br>, bullet / numbered lists and table rows (cells
// tab-separated). Uses DOMParser in the browser; falls back to the
// server's plain-text version (section.text) where there is no DOM.
export const htmlToPlainText = (html, fallbackText = "") => {
    if (!html) return "";
    if (typeof DOMParser === "undefined") return fallbackText || "";
    const root = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html").body.firstChild;
    const BLOCK = new Set(["P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6", "UL", "OL", "LI",
        "TABLE", "TR", "BLOCKQUOTE", "PRE", "SECTION", "ARTICLE"]);
    let out = "";
    const nl = () => { if (out && !out.endsWith("\n")) out += "\n"; };
    const walk = (node, ctx) => {
        if (node.nodeType === 3) { out += node.nodeValue.replace(/\s+/g, " "); return; }
        if (node.nodeType !== 1) return;
        const tag = node.tagName;
        if (tag === "SCRIPT" || tag === "STYLE") return;
        if (tag === "BR") { out += "\n"; return; }
        if (tag === "LI") {
            nl();
            const indent = "   ".repeat(Math.max(0, ctx.depth - 1));
            const ordered = ctx.list === "OL";
            // Quill writes nested / ordered items as <li data-list="bullet|ordered">.
            const dataList = node.getAttribute("data-list");
            const isOrdered = dataList ? dataList === "ordered" : ordered;
            ctx.counter = (ctx.counter || 0) + 1;
            out += indent + (isOrdered ? `${ctx.counter}. ` : "• ");
            node.childNodes.forEach((c) => walk(c, { ...ctx }));
            nl();
            return;
        }
        if (tag === "UL" || tag === "OL") {
            nl();
            const inner = { list: tag, depth: (ctx.depth || 0) + 1, counter: 0 };
            node.childNodes.forEach((c) => walk(c, inner));
            nl();
            return;
        }
        if (tag === "TD" || tag === "TH") {
            if (node.previousElementSibling) out += "\t";
            node.childNodes.forEach((c) => walk(c, ctx));
            return;
        }
        const block = BLOCK.has(tag);
        if (block) nl();
        node.childNodes.forEach((c) => walk(c, ctx));
        if (block) nl();
    };
    walk(root, { depth: 0 });
    return out.split("\n").map((l) => l.replace(/[ \t]+$/g, "")).join("\n")
        .replace(/\n{3,}/g, "\n\n").trim();
};

const todayUs = () => {
    const d = new Date();
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};

/**
 * Build the DMS document PDF from GET /api/dms/{id}/pdf-data.
 * Async only because the brand logo is loaded as an image.
 * @returns {Promise<jsPDF>}
 */
export const buildDmsPdf = async (pdfData, logoType) => {
    const d = pdfData || {};
    const isEcn = d.template === "ecn";
    const brand = resolveDmsBrand(logoType || d.logoType);
    const brandLogo = await loadBrandLogo(brand.logo);   // null → typeset fallback
    const BRAND = brand.colors.primary;

    // compress: deflate the content streams — the PDF is stored in the
    // database and attached to every approval e-mail, so size matters.
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter", compress: true });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const M = 40;
    // Go-live parity (2026-09-24): SOP (1) and WI (2) carry the legacy
    // uncontrolled-copy statement on every page, above the footer line
    // (DmsDB.vb:419-423 / DMSNew.vb:2419-2423 report parameter "uncontrolled").
    const DISCLAIMER_SIZE = 7, DISCLAIMER_LH = 8.5;
    const docTypeNum = Number(d.docType);
    let disclaimerLines = [];
    if (docTypeNum === 1 || docTypeNum === 2) {
        const disclaimer =
            `Printed copies of this document are only valid for the day printed (${todayUs()}), after which all printed copies are considered ‘Uncontrolled Copies’. An electronic controlled copy of the latest released revision of the document can be accessed through the SSW internal Document Management System (DMS).\n` +
            "Verify correct revision within DMS before use.";
        doc.setFont("helvetica", "italic").setFontSize(DISCLAIMER_SIZE);
        disclaimerLines = disclaimer.split("\n").flatMap((p) => doc.splitTextToSize(pdfSafe(p), pageW - M * 2));
    }
    const BOTTOM = 52                  // room for the per-page footer
        + (disclaimerLines.length ? disclaimerLines.length * DISCLAIMER_LH + 6 : 0);
    const contentW = pageW - M * 2;
    const INK = 33, MUTED = 110;
    let y = M;

    const ensureSpace = (h) => {
        if (y + h > pageH - BOTTOM) { doc.addPage(); y = M; }
    };

    // ── Header band — logo (left) + doc-type title / No. (right) ─────
    let leftBottom = y;
    if (brandLogo) {
        const logoH = brand.logo.height || 32;
        const logoW = logoH * (brandLogo.w / brandLogo.h);
        doc.addImage(brandLogo.dataUrl, "PNG", M, y, logoW, logoH, "brand-logo", "FAST");
        if (Array.isArray(brand.logoTextLockup) && brand.logoTextLockup.length) {
            let ty = y + 13;
            brand.logoTextLockup.forEach((ln) => {
                doc.setFont("helvetica", ln.bold ? "bold" : "normal").setFontSize(ln.size).setTextColor(...ln.color);
                doc.text(ln.text, M + logoW + 10, ty);
                ty += ln.size + 2;
            });
        }
        leftBottom = y + logoH;
    } else {
        let ty = y + 6;
        brand.headerLines.forEach((ln) => {
            doc.setFont("helvetica", ln.bold ? "bold" : "normal").setFontSize(ln.size).setTextColor(...ln.color);
            doc.text(ln.text, M, ty);
            ty += ln.size + 3;
        });
        leftBottom = ty - 6;
    }
    // Tester feedback (2026-10-01): the "No. WI-17134" line under the title was
    // redundant (the number is in the table below); the label is "Number".
    doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(INK);
    const titleLines = doc.splitTextToSize(pdfSafe(d.docTypeTitle || "DOCUMENT"), contentW * 0.55);
    doc.text(titleLines, pageW - M, y + 10, { align: "right" });
    y = Math.max(leftBottom, y + 10 + (titleLines.length - 1) * 15) + 12;
    doc.setDrawColor(...BRAND).setLineWidth(1.2);
    doc.line(M, y, pageW - M, y);
    y += 14;

    // ── Shared table styling ─────────────────────────────────────────
    const tableBase = {
        margin: { left: M, right: M, top: M, bottom: BOTTOM },
        theme: "grid",
        styles: {
            font: "helvetica", fontSize: 8.5, cellPadding: 4, textColor: INK,
            lineColor: [200, 200, 200], lineWidth: 0.5, valign: "top", overflow: "linebreak",
        },
        headStyles: { fillColor: brand.colors.tableHead, textColor: 255, fontStyle: "bold", fontSize: 8.5 },
    };
    const labelCell = (t) => ({ content: pdfSafe(t), styles: { fontStyle: "bold", textColor: MUTED, fillColor: [245, 246, 250] } });
    const runTable = (opts) => {
        autoTable(doc, { ...tableBase, startY: y, ...opts,
            styles: { ...tableBase.styles, ...(opts.styles || {}) } });
        y = doc.lastAutoTable.finalY + 12;
    };
    const emptyRow = (text, span) => [{ content: pdfSafe(text), colSpan: span, styles: { textColor: MUTED, fontStyle: "italic" } }];
    // Go-live parity (2026-09-24): DMS#34 — legacy printed the revision as two
    // digits ("Rev 01", DmsDB.vb:387). Display only; the stored rev is unchanged.
    // 2026-10-01 (tester): every revision on the printout has two digits, also in
    // the Associated Documents and Revision History tables (formatRev).
    const revText = (r) => formatRev(r);
    // Go-live parity (2026-09-24): DMS#23 — QSI-imported history text (legacy
    // FillDoc showed it in place of the empty grids).
    const qsiRow = (text, span) => [{ content: pdfSafe(text), colSpan: span, styles: { fontStyle: "normal" } }];

    // ── Metadata grid (template-specific labels) ─────────────────────
    const metaBody = isEcn
        ? [
            [labelCell("Title:"), { content: pdfSafe(d.title), colSpan: 3, styles: { fontStyle: "bold" } }],
            [labelCell("Number:"), pdfSafe(d.docId), labelCell("Revision:"), pdfSafe(revText(d.rev))],
            [labelCell("Document Edited By:"), pdfSafe(d.authorName), labelCell("Status:"), pdfSafe(d.statusText)],
            [labelCell("Document Manager:"), pdfSafe(d.managerName), labelCell("Date Needed:"), pdfSafe(d.ecn?.dateNeeded)],
            [labelCell("Original Creation Date:"), pdfSafe(d.createdTime), labelCell("Implementation Date:"), pdfSafe(d.implementationDate)],
        ]
        : [
            [labelCell("Title:"), { content: pdfSafe(d.title), colSpan: 3, styles: { fontStyle: "bold" } }],
            [labelCell("Number:"), pdfSafe(d.docId), labelCell("Revision:"), pdfSafe(revText(d.rev))],
            [labelCell("Author:"), pdfSafe(d.authorName), labelCell("Status:"), pdfSafe(d.statusText)],
            [labelCell("Manager:"), pdfSafe(d.managerName), labelCell("Created:"), pdfSafe(d.createdTime)],
            [labelCell("Implementation Date:"), { content: pdfSafe(d.implementationDate), colSpan: 3 }],
        ];
    runTable({
        body: metaBody,
        columnStyles: { 0: { cellWidth: 118 }, 2: { cellWidth: 108 } },
    });

    // ── ECN: parts table + customer block ────────────────────────────
    if (isEcn) {
        const parts = Array.isArray(d.ecn?.partsRows) ? d.ecn.partsRows : [];
        runTable({
            head: [["Part Description", "Part Number", "Drawing Number", "Customer Part No", "Current Rev", "New Rev"]],
            body: parts.length
                ? parts.map((r) => (r || []).slice(0, 6).map(pdfSafe))
                : [emptyRow("No product information", 6)],
            columnStyles: {
                0: { cellWidth: contentW * 0.26 }, 1: { cellWidth: contentW * 0.15 }, 2: { cellWidth: contentW * 0.15 },
                3: { cellWidth: contentW * 0.18 }, 4: { cellWidth: contentW * 0.13 }, 5: { cellWidth: contentW * 0.13 },
            },
        });
        runTable({
            body: [
                [labelCell("Customer Involvement"), pdfSafe(d.ecn?.customerInvolvement)],
                [labelCell("Customer Approval Required"), pdfSafe(d.ecn?.customerApprovalRequired)],
                [labelCell("Customer Notification Required"), pdfSafe(d.ecn?.customerNotificationRequired)],
                [labelCell("Customer Approval Notes"), pdfSafe(d.ecn?.customerApprovalNotes)],
                [labelCell("Customer Notification Notes"), pdfSafe(d.ecn?.customerNotificationNotes)],
                // Go-live parity (2026-09-24): DMS#22 — legacy ECO notes panel.
                [labelCell("Customer Approval Received"), pdfSafe(d.ecn?.customerApprovalReceived)],
                [labelCell("Customer Notification Received"), pdfSafe(d.ecn?.customerNotificationReceived)],
                [labelCell("ECO Notes"), pdfSafe(d.ecn?.ecnNotes)],
            ],
            columnStyles: { 0: { cellWidth: 170 } },
        });
    }

    // ── Content sections (rich text → wrapped plain text) ────────────
    const LINE_H = 11.5;
    const sectionTitle = (label) => {
        ensureSpace(34);
        doc.setFont("helvetica", "bold").setFontSize(10.5).setTextColor(...BRAND);
        doc.text(pdfSafe(label), M, y + 10);
        doc.setDrawColor(...BRAND).setLineWidth(0.6);
        doc.line(M, y + 14, pageW - M, y + 14);
        y += 24;
    };
    // Go-live parity (2026-09-24, audit DMS#7): pictures, tables and
    // formatting are printed (legacy DevExpress report rendered the full
    // RTF). Plain text stays the fallback if a section cannot be laid out.
    const richCtx = {
        doc, autoTable, x: M, width: contentW, top: M,
        pos: { get y() { return y; }, set y(v) { y = v; } },
        ensureSpace, usableH: pageH - BOTTOM - M,
        ink: INK, muted: MUTED, baseSize: 9, pdfSafe, htmlToPlainText,
        tableBase, cache: createPictureCache(),
    };
    for (const s of (Array.isArray(d.sections) ? d.sections : [])) {
        sectionTitle(s.label);
        const startPage = doc.getNumberOfPages();
        const startY = y;
        let rendered = false;
        try {
            rendered = await renderRichHtml(s.html, richCtx);
        } catch (err) {
            console.warn(`DMS PDF: section "${s.label}" printed as plain text:`, err);
            // Drop anything half-drawn, then print the section as plain text.
            while (doc.getNumberOfPages() > startPage) doc.deletePage(doc.getNumberOfPages());
            doc.setPage(startPage);
            doc.setFillColor(255, 255, 255);
            doc.rect(0, startY, pageW, pageH - BOTTOM - startY, "F");
            y = startY;
            rendered = false;
        }
        if (rendered) { y += 8; continue; }
        const text = pdfSafe(htmlToPlainText(s.html, s.text) || s.text || "");
        doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(INK);
        for (const para of text.split("\n")) {
            const indent = (para.match(/^ +/) || [""])[0].length * 3;
            const wrapped = para.trim() === "" ? [""] : doc.splitTextToSize(para.replace(/\t/g, "    "), contentW - indent);
            for (const ln of wrapped) {
                ensureSpace(LINE_H);
                doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(INK);
                doc.text(ln, M + indent, y + 8);
                y += LINE_H;
            }
        }
        y += 8;
    }

    // ── Secondary tables start on a new page (template page-break) ───
    doc.addPage();
    y = M;
    const tableTitle = (label) => sectionTitle(label);

    tableTitle("Associated Documents");
    const assoc = Array.isArray(d.associatedDocuments) ? d.associatedDocuments : [];
    runTable({
        head: [["Number", "Title", "Rev", "Status"]],
        body: assoc.length
            ? assoc.map((a) => [pdfSafe(a.docId), pdfSafe(a.title), pdfSafe(revText(a.rev)), pdfSafe(a.status)])
            : d.qsiAssociatedText
                ? [qsiRow(d.qsiAssociatedText, 4)]   // Go-live parity (2026-09-24): DMS#23
                : [emptyRow("", 4)],
    });

    tableTitle("Attachments");
    const atts = Array.isArray(d.attachments) ? d.attachments : [];
    runTable({
        head: [["File Name", "Title", "Subject To Approval"]],
        body: atts.length
            ? atts.map((a) => [pdfSafe(a.fileName), pdfSafe(a.title), a.subjectToApproval ? "Yes" : "No"])
            : [emptyRow("No attachments", 3)],
    });

    if (isEcn) {
        tableTitle("ECN Tasks");
        const tasks = Array.isArray(d.tasks) ? d.tasks : [];
        runTable({
            head: [["Due", "Task", "Assignees", "Progress"]],
            body: tasks.length
                ? tasks.map((t) => [pdfSafe(t.due), pdfSafe(t.description), pdfSafe(t.assigneesText), pdfSafe(t.progress)])
                : [emptyRow("No tasks", 4)],
        });
    }

    tableTitle("Revision History");
    const revs = Array.isArray(d.revisionHistory) ? d.revisionHistory : [];
    runTable({
        head: [["Date", "Rev", "Sec/Par. Changed", "Change Made", "Changed By"]],
        body: revs.length
            ? revs.map((r) => [
                pdfSafe(r.date), pdfSafe(revText(r.rev)), pdfSafe(r.secParaChanged), pdfSafe(r.changeMade),
                pdfSafe(r.changedById ? `${r.changedByName} (${r.changedById})` : r.changedByName),
            ])
            : [emptyRow("No revision history logged yet", 5)],
    });

    tableTitle("Approvals & Notifications");
    const section = (t) => [{ content: t, colSpan: 3, styles: { fontStyle: "bold", fillColor: [245, 246, 250] } }];
    const headRow = (cells) => cells.map((c) => ({
        content: c.content ?? c, colSpan: c.colSpan || 1,
        styles: { fontStyle: "bold", fillColor: brand.colors.tableHead, textColor: 255 },
    }));
    const approvals = Array.isArray(d.approvals) ? d.approvals : [];
    const notifications = Array.isArray(d.notifications) ? d.notifications : [];
    const propBody = [section("Approvals"), headRow(["Approver", "Status", "Date"])];
    if (approvals.length) {
        approvals.forEach((a) => {
            propBody.push([pdfSafe(`${a.approverName} (${a.approverId})`), pdfSafe(a.status), pdfSafe(a.date)]);
            if (a.reason) propBody.push([{
                content: pdfSafe(`Reason: ${a.reason}`), colSpan: 3,
                styles: { fontStyle: "italic", textColor: [124, 29, 29], cellPadding: { left: 18, top: 4, right: 4, bottom: 4 } },
            }]);
        });
    } else if (d.qsiApprovalText) {
        propBody.push(qsiRow(d.qsiApprovalText, 3));   // Go-live parity (2026-09-24): DMS#23
    } else propBody.push(emptyRow("No approvals", 3));
    propBody.push(section("Notifications"));
    propBody.push(headRow([{ content: "Employee", colSpan: 3 }]));
    if (notifications.length) {
        notifications.forEach((n) => propBody.push([{ content: pdfSafe(`${n.name} (${n.empId})`), colSpan: 3 }]));
    } else propBody.push(emptyRow("No notifications", 3));
    runTable({ body: propBody });

    // ── Watermark + per-page footer on every page ────────────────────
    const pages = doc.getNumberOfPages();
    const printed = `This copy printed on ${todayUs()}`;
    for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        if (d.watermarkText) {
            doc.saveGraphicsState();
            doc.setGState(new doc.GState({ opacity: 0.08 }));
            doc.setFont("helvetica", "bold").setFontSize(96).setTextColor(0);
            const w = doc.getTextWidth(d.watermarkText);
            const c = Math.SQRT1_2;
            doc.text(d.watermarkText, pageW / 2 - (w / 2) * c, pageH / 2 + (w / 2) * c, { angle: 45 });
            doc.restoreGraphicsState();
        }
        const fy = pageH - 28;
        doc.setDrawColor(210).setLineWidth(0.5);
        doc.line(M, fy - 10, pageW - M, fy - 10);
        // Go-live parity (2026-09-24): SOP / WI uncontrolled-copy statement,
        // directly above the footer line (space reserved through BOTTOM).
        if (disclaimerLines.length) {
            doc.setFont("helvetica", "italic").setFontSize(DISCLAIMER_SIZE).setTextColor(MUTED);
            let dy = fy - 10 - 4 - (disclaimerLines.length - 1) * DISCLAIMER_LH;
            disclaimerLines.forEach((ln) => {
                doc.text(ln, M, dy);
                dy += DISCLAIMER_LH;
            });
        }
        doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(MUTED);
        doc.text(pdfSafe(d.footerLeft || ""), M, fy);
        doc.text(pdfSafe(d.footerCenter || ""), pageW / 2, fy, { align: "center" });
        doc.text(`${printed}  Page ${i} of ${pages}`, pageW - M, fy, { align: "right" });
    }

    return doc;
};

/** Fetch pdf-data, build the PDF, return { base64 (no data-URL prefix), rev, data }. */
export const buildDmsPdfFromApi = async (dmsId, api, logoType) => {
    const res = await api.get(`dms/${dmsId}/pdf-data`);
    const data = res?.data ?? {};
    const doc = await buildDmsPdf(data, logoType || data.logoType);
    const base64 = doc.output("datauristring").split(",")[1];
    return { base64, rev: data.rev, data, doc };
};

/** Base64 of the document PDF, without the data-URL prefix. */
export const getDmsPdfBase64 = async (dmsId, api, logoType) =>
    (await buildDmsPdfFromApi(dmsId, api, logoType)).base64;

/**
 * Build + store the PDF (POST dms/{id}/pdf). Resolves to the server's
 * { dmsId, rev, sizeBytes, sha256 } plus the built { base64, doc }.
 * Throws on failure — callers decide how to surface it.
 */
export const uploadDmsPdf = async (dmsId, api, logoType) => {
    const built = await buildDmsPdfFromApi(dmsId, api, logoType);
    const res = await api.post(`dms/${dmsId}/pdf`, {
        pdfBase64: built.base64,
        logoType: logoType || built.data.logoType || "sswhite",
        rev: built.rev,
    });
    return { ...(res?.data ?? {}), base64: built.base64, doc: built.doc };
};

/**
 * Go-live parity (2026-09-24): after an action that changes the document's
 * status (approve / deny / release / un-release / obsolete / de-obsolete /
 * void) the server drops the stored PDF, which still showed the old status
 * and the UNAPPROVED watermark. Rebuild it from the CURRENT data and store it
 * (branding = the document's stored logo), so Print PDF and the Ready-for-
 * Release / Released mails (the worker waits for it) get a PDF that matches
 * the new status. Fire-and-forget: never throws — a failure only means Print
 * rebuilds on demand and the mail carries the dashboard link instead.
 */
export const refreshStoredDmsPdf = async (dmsId, api, logoType) => {
    if (!dmsId) return false;
    try {
        await uploadDmsPdf(dmsId, api, logoType);
        return true;
    } catch (err) {
        console.warn("DMS PDF refresh after status change failed:", err?.response?.data || err);
        return false;
    }
};

export default buildDmsPdf;
